/**
 * 全局认证状态 (pinia)
 *
 * 之前登录态只存在于 App.vue 的局部 ref (mount 时读一次 localStorage),
 * LoginView 登录成功后没有任何机制通知 App → 首次登录后头部用户 pill/
 * 退出按钮/admin 菜单全部消失, 必须 F5。集中到 store 后两端共享同一响应式状态。
 */
import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  getMe,
  getStoredUser,
  isAuthenticated,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  type AuthUser,
} from '@/api/client'
import { clearUserPresetCache } from '@/composables/useUserStockPresets'

/** 注册 (纯 API 透传, 不依赖 store 状态, 提到模块级) */
async function register(payload: {
  username: string
  password: string
  email?: string
  invite_code?: string
}) {
  return apiRegister(payload)
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(getStoredUser())

  /** 登录并拉取用户信息 (写 localStorage + 更新响应式 user) */
  async function login(username: string, password: string) {
    await apiLogin({ username, password })
    user.value = await getMe()
  }

  /** 应用启动/刷新后恢复会话; 返回是否已登录 */
  async function restore(): Promise<boolean> {
    if (!isAuthenticated()) return false
    try {
      user.value = await getMe()
      return true
    } catch (err: any) {
      // 401 (refresh 也失败) 已由拦截器 clearAuth + 跳登录, 这里只清 user。
      // 网络抖动/5xx 等瞬时故障**不清凭据**: 刷新瞬间后端抖动就把有效 token
      // 清掉会把用户踢回登录页 (ALENS-88 同族问题)。保留缓存用户继续渲染,
      // 后续请求经拦截器刷新/重试自愈; 真失效时仍会走 401 登出。
      if (err?.response?.status === 401) {
        user.value = null
        return false
      }
      return true
    }
  }

  function logout() {
    apiLogout()
    // 清掉上一账号的持仓/自选预设缓存, 防止换号后串数据
    clearUserPresetCache()
    user.value = null
  }

  return { user, login, register, restore, logout }
})
